import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { WaveForm, WaveSurfer, Region } from "wavesurfer-react";
import RegionsPlugin from "wavesurfer.js/dist/plugin/wavesurfer.regions.min";
import TimelinePlugin from "wavesurfer.js/dist/plugin/wavesurfer.timeline.min";
import { useQuery } from "@tanstack/react-query";
import { Menu, Transition } from "@headlessui/react";
import {
  BackwardIcon,
  ChevronUpDownIcon,
  ForwardIcon,
  PauseIcon,
  PlayIcon,
  SpeakerWaveIcon,
} from "@heroicons/react/24/outline";
import { fetchAudioDataPeakApi } from "../../services/job.services";
import { useJobDetail, useJobMetaData } from "../../services/Jobs/useQueries";
import formatTime from "../../utils/formatTime";

const SELECTED_REGION_COLOR = "rgba(16,255,0, 0.5)";
const DEFAULT_REGION_COLOR = "rgba(101, 184, 146, 0.22)";
const INITIAL_HEIGHT = 100;
const SPEED_OPTIONS = ["0.25", "0.5", "0.75", "1", "1.25", "1.5", "1.75", "2"];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const JobAudioPreview = forwardRef(function JobAudioPreview(
  { jobId, segments = [], selectedSegmentId, onSelectSegment },
  ref
) {
  const wavesurferRef = useRef(null);
  const progressBarRef = useRef(null);
  const onSelectSegmentRef = useRef(onSelectSegment);
  const lastSeekedIdRef = useRef(null);
  const isPlayingRef = useRef(false);
  const playbackSettingsRef = useRef({ horizontalZoom: 1, volume: 50, trackSpeed: "1" });

  const [wsMounted, setWsMounted] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [totalDuration, setTotalDuration] = useState(0);
  const [horizontalZoom, setHorizontalZoom] = useState(1);
  const [volume, setVolume] = useState(50);
  const [trackSpeed, setTrackSpeed] = useState("1");

  useEffect(() => {
    onSelectSegmentRef.current = onSelectSegment;
  }, [onSelectSegment]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    playbackSettingsRef.current = { horizontalZoom, volume, trackSpeed };
  }, [horizontalZoom, volume, trackSpeed]);

  useEffect(() => {
    setAudioReady(false);
    setIsPlaying(false);
    setTotalDuration(0);
    lastSeekedIdRef.current = null;
  }, [jobId]);

  const plugins = useMemo(
    () => [
      {
        plugin: RegionsPlugin,
        options: { dragSelection: false },
      },
      {
        plugin: TimelinePlugin,
        options: { container: "#vocab-map-timeline" },
      },
    ],
    []
  );

  const regions = useMemo(
    () =>
      (segments || [])
        .filter((segment) => segment.start != null && segment.end != null)
        .map((segment) => ({
          id: String(segment.id),
          start: segment.start,
          end: segment.end,
          color:
            segment.id === selectedSegmentId
              ? SELECTED_REGION_COLOR
              : DEFAULT_REGION_COLOR,
          drag: false,
          resize: false,
        })),
    [segments, selectedSegmentId]
  );

  const jobDetailQuery = useJobDetail({
    queryConfig: {
      queryKey: [jobId],
      apiParams: { jobId },
      enabled: Boolean(jobId),
      staleTime: Infinity,
    },
  });

  const jobMetaQuery = useJobMetaData({
    queryConfig: {
      queryKey: [jobId],
      apiParams: { jobId },
      enabled: Boolean(jobId),
      staleTime: Infinity,
    },
  });

  const peaksQuery = useQuery({
    queryKey: ["vocab-map-peaks", jobId],
    enabled: Boolean(jobId && jobMetaQuery.data?.size && jobDetailQuery.data?.id),
    retry: false,
    staleTime: Infinity,
    queryFn: () => fetchAudioDataPeakApi({ id: jobId }),
  });

  const updateTimeUi = useCallback(() => {
    const wavesurfer = wavesurferRef.current;
    if (!wavesurfer) return;

    const currentTime = wavesurfer.getCurrentTime();
    const currentTimeEl = document.getElementById("vocab-map-currentTime");
    if (currentTimeEl) {
      currentTimeEl.innerText = formatTime(currentTime);
    }

    const duration = wavesurfer.getDuration() || 1;
    if (progressBarRef.current) {
      progressBarRef.current.style.width = `${(currentTime / duration) * 100}%`;
    }
  }, []);

  const handleWSMount = useCallback(
    (waveSurfer) => {
      wavesurferRef.current = waveSurfer;
      if (!waveSurfer) {
        setWsMounted(false);
        return;
      }

      waveSurfer.on("ready", () => {
        const settings = playbackSettingsRef.current;
        setTotalDuration(waveSurfer.getDuration());
        waveSurfer.params.scrollParent = true;
        waveSurfer.zoom(settings.horizontalZoom);
        waveSurfer.setHeight(INITIAL_HEIGHT);
        waveSurfer.setVolume(settings.volume / 100);
        waveSurfer.setPlaybackRate(Number(settings.trackSpeed));
        waveSurfer.drawBuffer();
        updateTimeUi();
        setAudioReady(true);
      });

      waveSurfer.on("finish", () => setIsPlaying(false));
      waveSurfer.on("pause", () => setIsPlaying(false));
      waveSurfer.on("audioprocess", updateTimeUi);
      waveSurfer.on("seek", () => {
        setIsPlaying(false);
        waveSurfer.pause();
        updateTimeUi();
      });
      waveSurfer.on("region-click", (region, event) => {
        if (event) event.stopPropagation();
        const segmentId = Number(region.id);
        if (!Number.isNaN(segmentId)) {
          onSelectSegmentRef.current?.(segmentId);
        }
        waveSurfer.setCurrentTime(region.start);
        waveSurfer.pause();
        setIsPlaying(false);
        updateTimeUi();
      });

      setWsMounted(true);
    },
    [updateTimeUi]
  );

  useEffect(() => {
    if (!wsMounted || !wavesurferRef.current || !peaksQuery.data || !jobId) {
      return;
    }
    wavesurferRef.current.load(
      `${process.env.REACT_APP_BACKEND_URL}/jobs/${jobId}/data?org=&type=chunk&quality=compressed&number=0`,
      peaksQuery.data?.data
    );
  }, [wsMounted, peaksQuery.data, jobId]);

  useEffect(() => {
    if (!audioReady || !wavesurferRef.current) return;
    const segment = segments.find((item) => item.id === selectedSegmentId);
    if (segment?.start == null) return;
    if (lastSeekedIdRef.current === selectedSegmentId) return;

    lastSeekedIdRef.current = selectedSegmentId;
    wavesurferRef.current.setCurrentTime(segment.start);
    wavesurferRef.current.pause();
    setIsPlaying(false);
    updateTimeUi();
  }, [audioReady, selectedSegmentId, segments, updateTimeUi]);

  const handlePlay = useCallback(() => {
    if (!wavesurferRef.current) return;
    wavesurferRef.current.playPause();
    setIsPlaying(!isPlayingRef.current);
  }, []);

  const seekToSegment = useCallback(
    (segmentId) => {
      const segment = segments.find((item) => item.id === segmentId);
      if (!wavesurferRef.current || !audioReady || segment?.start == null) return;
      lastSeekedIdRef.current = segmentId;
      wavesurferRef.current.setCurrentTime(segment.start);
      wavesurferRef.current.pause();
      setIsPlaying(false);
      updateTimeUi();
    },
    [audioReady, segments, updateTimeUi]
  );

  useImperativeHandle(ref, () => ({
    playPause: handlePlay,
    seekToSegment,
  }));

  const handleForward = () => {
    wavesurferRef.current?.skipForward(2);
  };

  const handleBackward = () => {
    wavesurferRef.current?.skipBackward(2);
  };

  const handleHorizontalZoomChange = (value) => {
    setHorizontalZoom(value);
    wavesurferRef.current?.zoom(value);
  };

  const handleVolumeChange = (value) => {
    setVolume(value);
    wavesurferRef.current?.setVolume(value / 100);
  };

  const isLoading =
    jobDetailQuery.isLoading ||
    jobMetaQuery.isLoading ||
    peaksQuery.isFetching ||
    (Boolean(peaksQuery.data) && !audioReady);
  const hasError = jobDetailQuery.isError || jobMetaQuery.isError || peaksQuery.isError;
  const noAudio = jobMetaQuery.isSuccess && !jobMetaQuery.data?.size;

  return (
    <div className="mb-4 rounded-xl border border-gray-200 dark:border-ramsalab-light-navy bg-white dark:bg-ramsalab-navy p-4">
      {(isLoading || hasError || noAudio) && (
        <p className="mb-2 text-center text-sm text-gray-500">
          {hasError
            ? "Failed to load audio preview."
            : noAudio
            ? "No audio is available for this job."
            : "Please wait your audio is loading…"}
        </p>
      )}

      <WaveSurfer plugins={plugins} onMount={handleWSMount}>
        <WaveForm
          id="vocab-map-waveform"
          cursorColor="transparent"
          waveColor="#65B892"
          className="w-full"
          backend="MediaElement"
        >
          {regions.map((regionProps) => (
            <Region key={regionProps.id} {...regionProps} />
          ))}
        </WaveForm>
        <div
          id="vocab-map-timeline"
          className="dark:bg-ramsalab-deep-space dark:text-ramsalab-storm-gray"
        />
      </WaveSurfer>

      <p className="truncate dark:text-white text-center text-sm font-bold leading-6 my-2">
        <span id="vocab-map-currentTime">00:00</span> - {formatTime(totalDuration) || "00:00"}
      </p>
      <div className="relative w-full">
        <div className="bg-slate-100 dark:bg-ramsalab-midnight h-1.5 w-full rounded-lg" />
        <div
          className="bg-ramsalab-primary h-1.5 rounded-lg absolute top-0"
          ref={progressBarRef}
        />
      </div>

      <div className="grid grid-cols-6 items-center justify-between py-3">
        <div className="col-span-2" />
        <div className="col-span-2 flex justify-center items-center md:gap-4 gap-1">
          <button
            type="button"
            className="relative inline-flex items-center dark:bg-ramsalab-navy bg-white p-2 dark:text-white text-gray-400 hover:bg-gray-50 rounded-full focus:z-10"
            onClick={handleBackward}
            disabled={!audioReady}
          >
            <span className="sr-only">Backward</span>
            <BackwardIcon className="h-4 w-4 md:h-6 md:w-6 stroke-slate-500 dark:stroke-white" />
          </button>
          <button
            type="button"
            className="relative inline-flex items-center text-gray-400 p-2 focus:z-10 rounded-full dark:bg-ramsalab-gradient bg-slate-700 hover:bg-slate-900 focus:outline-none disabled:opacity-50"
            onClick={handlePlay}
            disabled={!audioReady}
          >
            <span className="sr-only">Play/ Pause</span>
            {isPlaying ? (
              <PauseIcon className="h-4 w-4 md:h-6 md:w-6 text-white" aria-hidden="true" />
            ) : (
              <PlayIcon className="h-4 w-4 md:h-6 md:w-6 text-white" aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            className="relative inline-flex items-center dark:bg-ramsalab-navy bg-white p-2 dark:text-white text-gray-400 hover:bg-gray-50 rounded-full focus:z-10"
            onClick={handleForward}
            disabled={!audioReady}
          >
            <span className="sr-only">Forward</span>
            <ForwardIcon className="h-4 w-4 md:h-6 md:w-6 dark:stroke-white stroke-slate-500" />
          </button>
        </div>

        <div className="col-span-2 flex items-center gap-2 justify-end">
          <Menu as="div" className="relative inline-block text-left">
            <Menu.Button className="relative inline-flex items-center dark:bg-ramsalab-navy bg-white pt-2 sm:p-2 text-gray-500 dark:text-white hover:bg-gray-50 dark:hover:bg-ramsalab-navy rounded-full focus:z-10">
              <ChevronUpDownIcon className="h-4 w-4 md:h-6 md:w-6 dark:stroke-white stroke-slate-500 rotate-90" />
            </Menu.Button>
            <Transition
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 origin-top-right rounded-md dark:bg-ramsalab-deep-space bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none p-2">
                <label className="text-sm font-medium leading-6 dark:text-white text-gray-900">
                  Horizontal Zoom
                </label>
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={horizontalZoom}
                  onChange={(e) => handleHorizontalZoomChange(parseInt(e.target.value, 10))}
                  className="h-6"
                />
              </Menu.Items>
            </Transition>
          </Menu>

          <Menu as="div" className="relative inline-block text-left">
            <Menu.Button className="relative inline-flex items-center dark:bg-ramsalab-navy bg-white pt-2 sm:p-2 text-gray-500 dark:text-white hover:bg-gray-50 dark:hover:bg-ramsalab-navy rounded-full focus:z-10">
              <SpeakerWaveIcon className="h-4 w-4 md:h-6 md:w-6 dark:stroke-white stroke-slate-500" />
            </Menu.Button>
            <Transition
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 origin-top-right rounded-md dark:bg-ramsalab-deep-space bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none p-2">
                <label className="text-sm font-medium leading-6 dark:text-white text-gray-900">
                  Volume
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={volume}
                  onChange={(e) => handleVolumeChange(parseInt(e.target.value, 10))}
                  className="h-6"
                />
              </Menu.Items>
            </Transition>
          </Menu>

          <Menu as="div" className="relative inline-block text-left">
            <Menu.Button className="relative inline-flex items-center dark:bg-ramsalab-navy bg-white sm:p-2 text-gray-500 dark:text-white sm:text-sm text-xs hover:bg-gray-50 dark:hover:bg-ramsalab-navy rounded-full focus:z-10">
              {trackSpeed}x
            </Menu.Button>
            <Transition
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 w-24 origin-top-right rounded-md dark:bg-ramsalab-deep-space bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  {SPEED_OPTIONS.map((val) => (
                    <Menu.Item key={val}>
                      {() => (
                        <button
                          type="button"
                          onClick={() => {
                            setTrackSpeed(val);
                            wavesurferRef.current?.setPlaybackRate(val);
                          }}
                          className={classNames(
                            val === trackSpeed
                              ? "bg-gray-100 dark:bg-ramsalab-green-translucent text-gray-900 dark:text-white"
                              : "text-gray-700 dark:text-white",
                            "block px-4 py-2 text-xs sm:text-sm w-full text-left dark:hover:bg-ramsalab-green-translucent hover:bg-gray-100 hover:text-gray-900"
                          )}
                        >
                          {val === "1" ? "Normal" : val}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  );
});

export default JobAudioPreview;
